// frontend/src/components/PixStep2.js
//
// Passo 2 do Cadastro de Clientes › Pix Santander (ETAPA 3b). Mesmo visual do Passo 2 do Asaas. Grava o cliente no
// banco do Souli (POST /api/pix/clients); NÃO cria nada no sistema Pix: o cliente vai para o Pix na hora da cobrança.

import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Smartphone, Users, Plus, Trash2, AlertCircle } from 'lucide-react';
import { getPixProducts, registerPixClient } from '../services/pixApi';
import './PixStep2.css';

const EMPTY_FORM = {
  name: '', document: '', email: '', phone: '', birthDate: '', seller: '', productId: '',
  zipCode: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: '',
};
const EMPTY_DEP = { nome: '', dataNascimento: '', grau: 'filho' };
const GRAU_LABELS = { filho: 'Filho(a)', conjuge: 'Cônjuge', pai: 'Pai/Mãe', agregado: 'Agregado' };
const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

const onlyDigits = (value) => String(value == null ? '' : value).replace(/\D/g, '');

function maskCpf(value) {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length > 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  if (d.length > 6) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  if (d.length > 3) return `${d.slice(0, 3)}.${d.slice(3)}`;
  return d;
}

function maskPhone(value) {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length > 10) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length > 6) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  if (d.length > 2) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return d;
}

const maskCep = (value) => {
  const d = onlyDigits(value).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

/** CPF com dígitos verificadores (a mesma regra do servidor). */
function isValidCpf(digits) {
  if (!/^\d{11}$/.test(digits) || /^(\d)\1{10}$/.test(digits)) return false;
  const check = (length) => {
    let sum = 0;
    for (let i = 0; i < length; i += 1) sum += Number(digits[i]) * (length + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return check(9) === Number(digits[9]) && check(10) === Number(digits[10]);
}

/** Data AAAA-MM-DD existente no calendário, entre 1900 e hoje. */
function isValidPastDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const exists = date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  return exists && year >= 1900 && date.getTime() <= Date.now();
}

const formatMoney = (value) => Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const calcAge = (birth) => {
  if (!birth) return '';
  return `(${Math.floor((Date.now() - new Date(birth).getTime()) / (1000 * 60 * 60 * 24 * 365))})`;
};

/** Confere o formulário no navegador (o servidor confere tudo de novo). Devolve { campo: mensagem }. */
function validateForm(form) {
  const errors = {};
  const name = form.name.replace(/\s+/g, ' ').trim();
  if (name.split(' ').filter(Boolean).length < 2 || name.length < 5) errors.name = 'Informe o nome completo.';
  if (!isValidCpf(onlyDigits(form.document))) errors.document = 'CPF inválido.';
  if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) errors.email = 'E-mail inválido.';
  let phone = onlyDigits(form.phone);
  if ((phone.length === 12 || phone.length === 13) && phone.startsWith('55')) phone = phone.slice(2);
  if (!/^\d{10,11}$/.test(phone)) errors.phone = 'Informe um celular ou telefone com DDD (10 ou 11 dígitos).';
  if (form.birthDate && !isValidPastDate(form.birthDate)) errors.birthDate = 'Data de nascimento inválida.';
  if (onlyDigits(form.zipCode).length !== 8) errors.zipCode = 'CEP inválido (8 dígitos).';
  if (form.street.trim().length < 2) errors.street = 'Informe o logradouro.';
  if (!form.number.trim()) errors.number = 'Informe o número (ou "S/N").';
  if (form.neighborhood.trim().length < 2) errors.neighborhood = 'Informe o bairro.';
  if (form.city.trim().length < 2) errors.city = 'Informe a cidade.';
  if (!UFS.includes(form.state.trim().toUpperCase())) errors.state = 'UF inválida.';
  if (!form.productId) errors.productId = 'Escolha o plano.';
  return errors;
}

/** Texto para o usuário a partir do erro da chamada. */
function describeSaveError(error) {
  switch (error && error.code) {
    case 'PIX_ACCESS_DENIED':
      return 'Seu usuário não tem permissão para cadastrar clientes Pix. Peça a um administrador a permissão "Cadastro de Clientes".';
    case 'CLIENT_EXISTS': {
      const existing = error.data && error.data.client && error.data.client.full_name;
      return `Já existe um cliente com este CPF no Souli${existing ? ` (${existing})` : ''}. Procure-o em Consulta de Clientes.`;
    }
    case 'PIX_NOT_CONFIGURED':
      return 'A integração com o Pix ainda não está configurada no servidor. Avise o administrador.';
    case 'PIX_LOGIN_FAILED':
    case 'PIX_AUTH_REJECTED':
      return 'O Pix recusou a integração. Avise o administrador.';
    case 'PIX_UNREACHABLE':
    case 'PIX_TIMEOUT':
      return 'Não consegui confirmar o plano no Pix agora (o Pix não respondeu). Nada foi salvo; tente de novo em instantes.';
    case 'VALIDATION':
      return 'Confira os campos marcados.';
    default:
      return (error && error.message) || 'Não foi possível salvar o cadastro. Tente novamente.';
  }
}

export default function PixStep2({ onBack, onSaved, onSaveAndCharge }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [topError, setTopError] = useState('');
  const [products, setProducts] = useState([]);
  const [productsState, setProductsState] = useState('loading'); // loading | ok | error
  const [productsError, setProductsError] = useState('');
  const [depForm, setDepForm] = useState(EMPTY_DEP);
  const [dependents, setDependents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const submittingRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const loadProducts = async () => {
    setProductsState('loading');
    setProductsError('');
    try {
      const list = await getPixProducts();
      setProducts(list);
      setProductsState('ok');
      if (list.length === 1) setForm((previous) => ({ ...previous, productId: list[0].id }));
    } catch (error) {
      setProductsError(describeSaveError(error));
      setProductsState('error');
    }
  };

  useEffect(() => { loadProducts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (event) => {
    const { name, value } = event.target;
    let next = value;
    if (name === 'document') next = maskCpf(value);
    else if (name === 'phone') next = maskPhone(value);
    else if (name === 'zipCode') next = maskCep(value);
    else if (name === 'state') next = value.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase();
    setForm((previous) => ({ ...previous, [name]: next }));
    if (errors[name]) setErrors((previous) => ({ ...previous, [name]: undefined }));
    if (topError) setTopError('');
  };

  const addDependent = () => {
    if (!depForm.nome.trim()) return;
    setDependents((previous) => [...previous, { ...depForm, nome: depForm.nome.trim(), id: Date.now() + previous.length }]);
    setDepForm(EMPTY_DEP);
  };
  const removeDependent = (id) => setDependents((previous) => previous.filter((d) => d.id !== id));

  const buildPayload = () => ({
    name: form.name.trim(),
    document: onlyDigits(form.document),
    email: form.email.trim(),
    phone: onlyDigits(form.phone),
    birthDate: form.birthDate,
    zipCode: onlyDigits(form.zipCode),
    street: form.street.trim(),
    number: form.number.trim(),
    complement: form.complement.trim(),
    neighborhood: form.neighborhood.trim(),
    city: form.city.trim(),
    state: form.state.trim().toUpperCase(),
    productId: form.productId,
    seller: form.seller.trim(),
    dependents: dependents.map(({ nome, dataNascimento, grau }) => ({ nome, dataNascimento, grau })),
  });

  const submit = async (mode) => {
    if (submittingRef.current) return; // clique duplo: só uma gravação por vez
    setTopError('');
    const found = validateForm(form);
    if (Object.keys(found).length) {
      setErrors(found);
      setTopError('Confira os campos marcados.');
      return;
    }
    submittingRef.current = true;
    setSaving(true);
    try {
      const result = await registerPixClient(buildPayload());
      setSaving(false);
      setSavedOk(true);
      timerRef.current = setTimeout(() => {
        setSavedOk(false);
        if (mode === 'charge' && onSaveAndCharge) onSaveAndCharge(result.client);
        else if (onSaved) onSaved(result.client);
      }, 1500);
    } catch (error) {
      submittingRef.current = false;
      setSaving(false);
      if (error && error.fields) setErrors(error.fields);
      setTopError(describeSaveError(error));
    }
  };

  const renderField = (name, label, input) => (
    <div className={`s2-field ${errors[name] ? 'pix-campo-erro' : ''}`}>
      <label htmlFor={`pix-${name}`}>{label}</label>
      {input}
      {errors[name] && <span className="pix-erro-campo" role="alert">{errors[name]}</span>}
    </div>
  );
  const text = (name, extra = {}) => (
    <input id={`pix-${name}`} type="text" name={name} value={form[name]} onChange={handleChange} aria-invalid={!!errors[name]} {...extra} />
  );
  const busy = saving || savedOk;

  return (
    <div className="clients-view">
      {(saving || savedOk) && (
        <div className="save-overlay">
          <div className="save-overlay-box">
            {saving && <><div className="save-spinner" /><p>Salvando cadastro...</p></>}
            {savedOk && <><div className="save-check">✓</div><p>Cadastro salvo!</p></>}
          </div>
        </div>
      )}

      <div className="step2-topbar">
        <div className="step2-topbar-left" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
          <div className="step-badge pix-step-badge"><Smartphone size={15} /> Pix Santander</div>
          <h2 className="view-title" style={{ margin: 0 }}>Cadastro de Clientes — Dados do Cliente</h2>
          <p className="step-description" style={{ margin: 0 }}>Preencha os dados do cliente para cadastrá-lo no Pix Santander</p>
        </div>
        <button type="button" className="back-button" style={{ margin: 0, alignSelf: 'flex-start' }} onClick={onBack}>
          <ArrowLeft size={16} /> Voltar
        </button>
      </div>

      <form onSubmit={(event) => event.preventDefault()} noValidate>
        <div className="cpfl-form-card">
          <div className="t3-row" style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '14px' }}>
            {renderField('name', 'NOME COMPLETO *', text('name'))}
            {renderField('document', 'CPF *', text('document', { inputMode: 'numeric', placeholder: '000.000.000-00' }))}
            {renderField('email', 'E-MAIL', <input id="pix-email" type="email" name="email" value={form.email} onChange={handleChange} aria-invalid={!!errors.email} />)}
            {renderField('productId', 'PLANO *', (
              <>
                <select id="pix-productId" name="productId" value={form.productId} onChange={handleChange} disabled={productsState !== 'ok'} aria-invalid={!!errors.productId}>
                  <option value="">{productsState === 'loading' ? 'Carregando planos...' : 'Selecione'}</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{`${p.name} — ${formatMoney(p.amount)}`}</option>)}
                </select>
                {productsState === 'error' && (
                  <span className="pix-erro-campo" role="alert">
                    {productsError} <button type="button" className="pix-link-btn" onClick={loadProducts}>Tentar de novo</button>
                  </span>
                )}
              </>
            ))}
          </div>

          <div className="t3-row" style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
            {renderField('phone', 'CELULAR / TELEFONE *', <input id="pix-phone" type="tel" name="phone" value={form.phone} onChange={handleChange} aria-invalid={!!errors.phone} placeholder="(00) 00000-0000" />)}
            {renderField('birthDate', 'DATA DE NASCIMENTO', <input id="pix-birthDate" type="date" name="birthDate" value={form.birthDate} onChange={handleChange} aria-invalid={!!errors.birthDate} />)}
            {renderField('seller', 'VENDEDOR', text('seller', { placeholder: 'Nome do vendedor' }))}
          </div>

          <div className="cpfl-divider" />

          <div className="step3-inline-title" style={{ marginBottom: 16 }}>
            <Smartphone size={16} /> <strong>Endereço (obrigatório para o Pix)</strong>
          </div>
          <div className="t3-row" style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '2fr 0.8fr 1.2fr', gap: '14px' }}>
            {renderField('street', 'LOGRADOURO *', text('street'))}
            {renderField('number', 'NÚMERO *', text('number'))}
            {renderField('complement', 'COMPLEMENTO', text('complement'))}
          </div>
          <div className="t3-row" style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr 0.7fr 0.8fr', gap: '14px' }}>
            {renderField('neighborhood', 'BAIRRO *', text('neighborhood'))}
            {renderField('city', 'CIDADE *', text('city'))}
            {renderField('state', 'UF *', text('state', { maxLength: 2 }))}
            {renderField('zipCode', 'CEP *', text('zipCode', { inputMode: 'numeric', placeholder: '00000-000' }))}
          </div>

          <div className="cpfl-divider" />

          <div className="step3-section">
            <div className="step3-section-title"><Users size={18} /> Dependentes</div>
            <div className="dep-add-row">
              <div className="s2-field" style={{ flex: 1, minWidth: 220 }}>
                <label htmlFor="pix-dep-nome">NOME COMPLETO</label>
                <input id="pix-dep-nome" type="text" value={depForm.nome} onChange={(e) => setDepForm((p) => ({ ...p, nome: e.target.value }))} placeholder="Nome completo do dependente" />
              </div>
              <div className="s2-field" style={{ width: 160, flexShrink: 0 }}>
                <label htmlFor="pix-dep-nasc">DATA DE NASCIMENTO</label>
                <input id="pix-dep-nasc" type="date" value={depForm.dataNascimento} onChange={(e) => setDepForm((p) => ({ ...p, dataNascimento: e.target.value }))} />
              </div>
              <div className="s2-field" style={{ width: 180, flexShrink: 0 }}>
                <label htmlFor="pix-dep-grau">GRAU DE PARENTESCO</label>
                <select id="pix-dep-grau" value={depForm.grau} onChange={(e) => setDepForm((p) => ({ ...p, grau: e.target.value }))}>
                  <option value="filho">Filho(a)</option>
                  <option value="conjuge">Cônjuge</option>
                  <option value="pai">Pai / Mãe</option>
                  <option value="agregado">Agregado</option>
                </select>
              </div>
              <button type="button" className="btn-add-dep" onClick={addDependent}><Plus size={16} /> Adicionar</button>
            </div>
            {errors.dependents && <span className="pix-erro-campo" role="alert">{errors.dependents}</span>}
            {dependents.length > 0 && (
              <div className="dep-table-wrapper">
                <table className="dep-table">
                  <thead><tr><th>#</th><th>Nome</th><th>Nascimento</th><th>Grau</th><th>Opções</th></tr></thead>
                  <tbody>
                    {dependents.map((d, i) => (
                      <tr key={d.id} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                        <td className="dep-num">{i + 1}</td>
                        <td className="dep-nome">
                          <div className="td-name-inner">
                            <div className="mini-avatar dep-avatar">{d.nome.charAt(0)}</div>
                            {d.nome.toUpperCase()}
                          </div>
                        </td>
                        <td className="dep-nasc">
                          {calcAge(d.dataNascimento)}{' '}
                          {d.dataNascimento ? new Date(`${d.dataNascimento}T00:00:00`).toLocaleDateString('pt-BR') : '—'}
                        </td>
                        <td><span className={`grau-badge grau-${d.grau}`}>{GRAU_LABELS[d.grau]}</span></td>
                        <td>
                          <button type="button" className="btn-dep-action excluir" onClick={() => removeDependent(d.id)}>
                            <Trash2 size={13} /> Excluir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {topError && (
            <div className="pix-banner-erro" role="alert">
              <AlertCircle size={16} /> <span>{topError}</span>
            </div>
          )}

          <div className="cpfl-actions">
            <button type="button" className="btn-cancel-edit" onClick={onBack} disabled={busy}>Cancelar</button>
            <button type="button" className="btn-save-only" onClick={() => submit('save')} disabled={busy || productsState !== 'ok'}>💾 Salvar Cadastro</button>
            <button type="button" className="btn-save-charge" onClick={() => submit('charge')} disabled={busy || productsState !== 'ok'}>⚡ Salvar e Gerar Cobrança</button>
          </div>
        </div>
      </form>
    </div>
  );
}
